// src/constants/reportPeriods.ts
//
// The period presets a composed report can be created, edited and previewed
// with (plan 2026-09-02 fill-with-chat-report-blocks, R4; the full catalogue
// since plan 2026-09-13-report-builder-parity-workstreams W2.1, ADR-0267).
// Mirrors the backend's preset table (`services/reports/periods.py`,
// `preset_catalog()`) — customer-facing labels only, the key itself is what
// travels on the wire. A pytest text-pin (`tests/test_period_vocabulary.py`)
// checks that every catalogue name is spelled in this file.
//
// One list so the "Build from blocks" drawer's Select, the Settings pane, a
// report block's preview parameter line and the panel's period display can
// never show different words for the same preset.
//
// Three kinds of window, three groups in the Select:
//   - a COMPLETE period (last week, last month, …): the report reads the
//     same every day until the period rolls over;
//   - a period SO FAR (this month, this year, …): ends yesterday, grows daily;
//   - a ROLLING window: the last N days ending yesterday, or — through the
//     `rolling_<n>_<unit>` spelling (`comparisonWindow.ts`) — N whole weeks
//     ending last Sunday or N whole months ending last month.
//
// The report's to-date ALIASES (`wtd` / `mtd` / `ytd`) are the same windows
// as "This week" / "This month" / "This year": labelled here for a report
// that carries one, never offered — one label per window.

import { parseRollingPreset, rollingPresetLabel } from "@/utils/comparisonWindow";

/** A catalogue name or a rolling spelling. Validated server-side by
 *  `report_period_key`; the client only needs to label it. */
export type ReportPeriodPreset = string;

export type ReportPeriodOption = { value: string; label: string };

export const REPORT_PERIOD_GROUPS: { group: string; items: ReportPeriodOption[] }[] = [
  {
    group: "Complete periods",
    items: [
      { value: "yesterday", label: "Yesterday" },
      { value: "last_week", label: "Last week" },
      { value: "last_month", label: "Last month" },
      { value: "last_quarter", label: "Last quarter" },
      { value: "last_year", label: "Last year" },
    ],
  },
  {
    group: "So far",
    items: [
      { value: "today", label: "Today" },
      { value: "this_week", label: "This week" },
      { value: "this_month", label: "This month" },
      { value: "this_quarter", label: "This quarter" },
      { value: "this_year", label: "This year" },
    ],
  },
  {
    group: "Rolling",
    items: [
      { value: "last_7_days", label: "Last 7 days" },
      { value: "last_14_days", label: "Last 14 days" },
      { value: "last_28_days", label: "Last 28 days" },
      { value: "last_30_days", label: "Last 30 days" },
      { value: "last_90_days", label: "Last 90 days" },
      { value: "last_365_days", label: "Last 365 days" },
    ],
  },
];

/** Every offered preset, flat, in the Select's order. */
export const REPORT_PERIOD_OPTIONS: ReportPeriodOption[] = REPORT_PERIOD_GROUPS.flatMap((g) => g.items);

export const REPORT_PERIOD_LABELS: Record<string, string> = {
  ...Object.fromEntries(REPORT_PERIOD_OPTIONS.map((o) => [o.value, o.label])),
  // The aliases: understood, labelled, not offered (see the header).
  wtd: "Week to date",
  mtd: "Month to date",
  ytd: "Year to date",
};

/** Customer-facing label for a period preset key: a catalogue name, a
 *  rolling spelling ("Last 4 weeks"), or — for a retired preset, or a
 *  payload from a newer backend — a humanised version of the key itself
 *  rather than nothing. */
export function reportPeriodLabel(preset: string | null | undefined): string {
  if (!preset) return "";
  const rolling = parseRollingPreset(preset);
  if (rolling) return rollingPresetLabel(rolling);
  if (REPORT_PERIOD_LABELS[preset]) return REPORT_PERIOD_LABELS[preset];
  return preset
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}
